import type {
  BuildingInstanceV1,
  CaseManifestV1,
  ClueV1,
  NpcInstanceV1,
} from "./case-manifest.js";

/** Clue row may use title and/or display_name from seeds. */
export type ClueLike = ClueV1 & {
  display_name?: string;
  name?: string;
  related_npc_instance_id?: string | null;
};

/** Building row may use name / label from seeds before normalization. */
export type BuildingLike = BuildingInstanceV1 & {
  name?: string;
  label?: string;
};

export const SKELETON_BUILDING_DESCRIPTION = "A location in the case.";

export const UNTITLED_CASE_TITLE = "Untitled Case";

/** Raw title from manifest fields before catalog backfill or untitled sentinel. */
export function rawCaseTitleFromManifest(manifest: CaseManifestV1): string {
  if (typeof manifest.title === "string" && manifest.title.trim()) {
    return manifest.title.trim();
  }
  const legacy = manifest as unknown as { case_title?: unknown; caseTitle?: unknown };
  if (typeof legacy.case_title === "string" && legacy.case_title.trim()) {
    return legacy.case_title.trim();
  }
  if (typeof legacy.caseTitle === "string" && legacy.caseTitle.trim()) {
    return legacy.caseTitle.trim();
  }
  return "";
}

/** Player-facing case title; uses legacy fields when `title` is empty. */
export function resolveCaseDisplayTitle(
  manifest: CaseManifestV1,
  fallback: string = UNTITLED_CASE_TITLE
): string {
  const t = rawCaseTitleFromManifest(manifest);
  if (!t || /^untitled\s+case$/i.test(t)) return fallback;
  return t;
}

const GENERIC_BUILDING_CATEGORIES = new Set([
  "services",
  "homes",
  "commerce",
  "misc",
  "retail",
  "stores",
  "industrial",
  "civic",
  "culture",
  "food",
  "nightlife",
  "office",
  "warehouse",
  "park",
  "transit",
]);

const TECHNICAL_BUILDING_ID = /^(city|suburb)_[a-z]+_\d+$/i;
const TECHNICAL_ENTITY_ID =
  /^(clue|npc|city|suburb)_[a-z0-9_]+$/i;

/** True when a string looks like an internal slug, not copy written for players. */
export function looksLikeTechnicalId(value: string): boolean {
  const v = value.trim();
  if (!v) return true;
  if (TECHNICAL_ENTITY_ID.test(v)) return true;
  if (/^clue_/i.test(v)) return true;
  if (/red_herring/i.test(v)) return true;
  return false;
}

function normalizeLabelKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Rejects titles produced from clue_id slugs (repair, humanizeId, or id.replace). */
export function looksLikeGeneratedClueTitle(label: string, clueId: string): boolean {
  const s = label.trim();
  if (!s) return true;
  if (/^\d{1,4}$/.test(s)) return true;
  if (/^[a-z]\d{1,2}$/i.test(s)) return true;
  if (/^clue\s/i.test(s)) return true;
  if (/\b(red\s*herring|redherring)\b/i.test(s)) return true;
  if (/^clue\s+(key|bg|rh|background|red)\b/i.test(s)) return true;
  const key = normalizeLabelKey(s);
  const idKey = normalizeLabelKey(clueId);
  if (key === idKey) return true;
  if (key === normalizeLabelKey(humanizeId(clueId))) return true;
  const idSansPrefix = normalizeLabelKey(clueId.replace(/^clue_/i, "").replace(/_/g, " "));
  if (key === idSansPrefix || key === normalizeLabelKey(`clue ${idSansPrefix}`)) return true;
  return false;
}

/** True when label is safe to show in HUD, journal, and toasts. */
export function isPlayerFacingLabel(label: string, entityId: string): boolean {
  const s = label.trim();
  if (!s) return false;
  if (s === entityId) return false;
  if (looksLikeTechnicalId(s)) return false;
  return true;
}

/** Player-facing clue title (stricter than generic entity labels). */
export function isCluePlayerFacingLabel(label: string, clueId: string): boolean {
  if (!isPlayerFacingLabel(label, clueId)) return false;
  if (looksLikeGeneratedClueTitle(label, clueId)) return false;
  return true;
}

function pickPlayerFacingLabel(
  entityId: string,
  candidates: Array<string | undefined | null>
): string | null {
  for (const raw of candidates) {
    const s = raw?.trim();
    if (s && isPlayerFacingLabel(s, entityId)) return s;
  }
  return null;
}

function pickCluePlayerFacingLabel(
  clueId: string,
  candidates: Array<string | undefined | null>
): string | null {
  for (const raw of candidates) {
    const s = raw?.trim();
    if (s && isCluePlayerFacingLabel(s, clueId)) return s;
  }
  return null;
}

function clueTitleFromDescription(description: string, maxLen = 56): string | null {
  const d = description.trim();
  if (!d) return null;
  const firstSentence = d.split(/(?<=[.!?])\s+/)[0]?.trim() ?? d;
  if (
    firstSentence.length <= maxLen &&
    !looksLikeGeneratedClueTitle(firstSentence, "") &&
    !looksLikeTechnicalId(firstSentence)
  ) {
    return firstSentence.replace(/[.,;:!?]+$/, "");
  }
  const chunk = d.slice(0, maxLen);
  const trimmed = chunk.replace(/\s+\S*$/, "").trim() || chunk.trim();
  const out = trimmed.replace(/[.,;:!?]+$/, "");
  if (!out || looksLikeGeneratedClueTitle(out, "") || looksLikeTechnicalId(out)) {
    return null;
  }
  return `${out}…`;
}

const GENERIC_CLUE_TITLE = "Evidence";

/** Generic player-facing title when manifest rows lack an explicit name. */
export function clueFallbackTitle(clue: ClueLike): string {
  const fromFields = pickCluePlayerFacingLabel(clue.clue_id, [
    clue.display_name,
    clue.title,
    clue.name,
  ]);
  if (fromFields) return fromFields;

  const fromDesc = clue.description
    ? clueTitleFromDescription(clue.description)
    : null;
  if (fromDesc) return fromDesc;

  return GENERIC_CLUE_TITLE;
}

/** Triangulation confirm button / summary line (never raw confirmed_clue_id slug). */
export function triangulationConfirmLabel(
  manifest: CaseManifestV1,
  pair: { confirmed_clue_id: string; confirmed_title: string }
): string {
  const fromTitle = pair.confirmed_title?.trim();
  if (fromTitle && isCluePlayerFacingLabel(fromTitle, pair.confirmed_clue_id)) {
    return fromTitle;
  }
  return clueDisplayName(manifest, pair.confirmed_clue_id);
}

export function humanizeId(id: string): string {
  const s = typeof id === "string" ? id : "";
  return s
    .replace(/^clue_key_/i, "")
    .replace(/^npc_/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function clueDisplayName(manifest: CaseManifestV1, clueId: string): string {
  const c = manifest.clues.find((x) => x.clue_id === clueId) as ClueLike | undefined;
  if (!c) return "New clue";
  return clueFallbackTitle(c);
}

export function npcInstanceDisplayName(npc: NpcInstanceV1): string {
  const picked = pickPlayerFacingLabel(npc.instance_id, [npc.name]);
  if (picked) return picked;
  const hum = humanizeId(npc.instance_id);
  if (hum && isPlayerFacingLabel(hum, npc.instance_id)) return hum;
  return "Unknown contact";
}

export function npcDisplayName(manifest: CaseManifestV1, instanceId: string): string {
  const n = manifest.npcs.find((x) => x.instance_id === instanceId);
  if (n) return npcInstanceDisplayName(n);
  const hum = humanizeId(instanceId);
  if (hum && isPlayerFacingLabel(hum, instanceId)) return hum;
  return "Unknown contact";
}

/** True when a label is only a zone category token (e.g. "Services"), not a place name. */
export function looksLikeGenericBuildingCategory(label: string): boolean {
  const s = label.trim();
  if (!s) return true;
  const words = s.split(/\s+/);
  if (words.length === 1 && GENERIC_BUILDING_CATEGORIES.has(words[0]!.toLowerCase())) {
    return true;
  }
  const categoryWords = words.filter(
    (w) => !/^\d+$/.test(w) && !/^(city|suburb)$/i.test(w)
  );
  if (
    categoryWords.length === 1 &&
    GENERIC_BUILDING_CATEGORIES.has(categoryWords[0]!.toLowerCase())
  ) {
    return true;
  }
  return false;
}

/** Strip trailing asset index parenthetical for HUD copy. */
export function trimAssetLabelForUi(label: string): string {
  const t = label.trim().replace(/\s*\(\d+\)\s*$/, "").trim();
  return t || label.trim();
}

const BUILDING_LABEL_SPLIT = /^(.{3,48}?)\s*[-—–:]\s+(.{12,})$/u;

const BUILDING_TITLE_MINOR_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "of",
  "with",
  "in",
  "on",
  "at",
  "to",
  "for",
  "by",
]);

/** True when label is asset-manifest prose, not a short proper place name. */
export function looksLikeAssetSlugBuildingLabel(label: string): boolean {
  const s = label.trim();
  if (!s) return true;
  if (s !== s.toLowerCase()) return false;
  const words = s.split(/\s+/);
  if (words.length > 6) return true;
  if (words.length >= 4) return true;
  return false;
}

function titleCaseBuildingWords(s: string): string {
  const words = s.trim().split(/\s+/);
  return words
    .map((w, i) => {
      if (/^\d/.test(w) || /^[A-Z]{2,}$/.test(w)) return w;
      const lower = w.toLowerCase();
      if (i > 0 && i < words.length - 1 && BUILDING_TITLE_MINOR_WORDS.has(lower)) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

/** Short proper place name from asset slug or dash-separated seed label. */
export function compactAssetStyleBuildingLabel(
  label: string,
  buildingId = ""
): string {
  const trimmed = trimAssetLabelForUi(label);
  if (!trimmed) return "Building";
  const dash = BUILDING_LABEL_SPLIT.exec(trimmed);
  if (dash?.[1]) {
    const left = dash[1].trim();
    if (
      left.length >= 3 &&
      left.length <= 48 &&
      (!buildingId || isPlayerFacingLabel(left, buildingId))
    ) {
      return titleCaseBuildingWords(left);
    }
  }
  const words = trimmed.split(/\s+/);
  const take =
    words.length >= 6 ? words.slice(0, 3) : words.slice(0, Math.min(words.length, 5));
  return titleCaseBuildingWords(take.join(" "));
}

function pickPlayerFacingBuildingLabel(
  buildingId: string,
  candidates: Array<string | undefined | null>
): string | null {
  for (const raw of candidates) {
    const s = raw?.trim();
    if (!s) continue;
    if (!isPlayerFacingLabel(s, buildingId)) continue;
    if (TECHNICAL_BUILDING_ID.test(s)) continue;
    if (looksLikeGenericBuildingCategory(s)) continue;
    if (looksLikeAssetSlugBuildingLabel(s)) continue;
    return s;
  }
  return null;
}

function buildingTitleFromDescription(description: string, maxLen = 56): string | null {
  const d = description.trim();
  if (!d) return null;
  const firstSentence = d.split(/(?<=[.!?])\s+/)[0]?.trim() ?? d;
  if (
    firstSentence.length <= maxLen &&
    !looksLikeTechnicalId(firstSentence) &&
    !looksLikeGenericBuildingCategory(firstSentence)
  ) {
    return firstSentence.replace(/[.,;:!?]+$/, "");
  }
  const chunk = d.slice(0, maxLen);
  const trimmed = chunk.replace(/\s+\S*$/, "").trim() || chunk.trim();
  const out = trimmed.replace(/[.,;:!?]+$/, "");
  if (!out || looksLikeTechnicalId(out) || looksLikeGenericBuildingCategory(out)) {
    return null;
  }
  return `${out}…`;
}

export function isPlayerFacingBuildingDescription(description: string): boolean {
  const d = description.trim();
  if (!d) return false;
  if (d === SKELETON_BUILDING_DESCRIPTION) return false;
  if (looksLikeTechnicalId(d)) return false;
  return true;
}

/** Short scene copy for investigation modal when manifest has real description. */
export function buildingLeadDescription(building: BuildingInstanceV1): string | null {
  const d = building.description?.trim();
  if (!d || !isPlayerFacingBuildingDescription(d)) return null;
  return d;
}

export type BuildingDisplayNameOptions = {
  assetLabel?: string;
};

function pickBuildingDisplayName(
  buildingId: string,
  row: BuildingLike,
  assetLabel?: string
): string | null {
  const trimmedAsset = assetLabel?.trim()
    ? trimAssetLabelForUi(assetLabel)
    : undefined;
  const fromFields = pickPlayerFacingBuildingLabel(buildingId, [
    row.display_name,
    row.name,
    row.label,
    trimmedAsset,
  ]);
  if (fromFields) return fromFields;

  const fromDesc =
    row.description && isPlayerFacingBuildingDescription(row.description)
      ? buildingTitleFromDescription(row.description)
      : null;
  if (fromDesc) return fromDesc;

  for (const raw of [row.display_name, row.name, row.label, trimmedAsset]) {
    const s = raw?.trim();
    if (s && looksLikeAssetSlugBuildingLabel(s)) {
      return compactAssetStyleBuildingLabel(s, buildingId);
    }
  }

  return null;
}

export function buildingDisplayNameForId(
  manifest: CaseManifestV1,
  buildingId: string,
  opts?: BuildingDisplayNameOptions
): string {
  const b = manifest.buildings.find((x) => x.building_id === buildingId);
  if (!b) return "Building";
  return finalizeBuildingDisplayName(buildingId, b as BuildingLike, opts?.assetLabel);
}

function finalizeBuildingDisplayName(
  buildingId: string,
  row: BuildingLike,
  assetLabel?: string
): string {
  const picked =
    pickBuildingDisplayName(buildingId, row, assetLabel) ?? "Building";
  if (looksLikeAssetSlugBuildingLabel(picked)) {
    return compactAssetStyleBuildingLabel(picked, buildingId);
  }
  return picked;
}

export function normalizeManifestBuildings(
  manifest: CaseManifestV1,
  assetLabelsByBuildingId: Record<string, string> = {}
): CaseManifestV1 {
  return {
    ...manifest,
    buildings: manifest.buildings.map((b) => {
      const name = finalizeBuildingDisplayName(
        b.building_id,
        b as BuildingLike,
        assetLabelsByBuildingId[b.building_id]
      );
      return { ...b, display_name: name };
    }),
  };
}

/** found_at is building_id or npc instance_id. */
export function resolveFoundAtLabel(manifest: CaseManifestV1, foundAt: string): string {
  const at = foundAt?.trim();
  if (!at) return "Unknown location";
  const building = manifest.buildings.find((b) => b.building_id === at);
  if (building) return buildingDisplayNameForId(manifest, building.building_id);
  const npc = manifest.npcs.find((n) => n.instance_id === at);
  if (npc) return npcDisplayName(manifest, npc.instance_id);
  if (looksLikeTechnicalId(at)) return "Unknown location";
  const hum = humanizeId(at);
  return hum && isPlayerFacingLabel(hum, at) ? hum : "Unknown location";
}

export function zoneDisplayNameFromManifest(
  manifest: CaseManifestV1,
  zone: "downtown" | "maple_heights"
): string {
  const z = manifest.zones?.find((x) => x.id === zone);
  return z?.display_name ?? (zone === "downtown" ? "Downtown" : "Maple Heights");
}

/** Timeline / journal line: place name plus zone, e.g. "Harbor Tea Merchant (Downtown)". */
export function resolveFoundAtDisplay(manifest: CaseManifestV1, foundAt: string): string {
  const at = foundAt?.trim();
  if (!at) return "Unknown location";
  const label = resolveFoundAtLabel(manifest, at);
  const building = manifest.buildings.find((b) => b.building_id === at);
  const npc = manifest.npcs.find((n) => n.instance_id === at);
  if (building || npc) {
    const zone = resolveFoundAtZone(manifest, at);
    return `${label} (${zoneDisplayNameFromManifest(manifest, zone)})`;
  }
  return label;
}

/** Zone for a found_at target (building_id or npc instance_id). */
export function resolveFoundAtZone(
  manifest: CaseManifestV1,
  foundAt: string
): "downtown" | "maple_heights" {
  const at = foundAt?.trim();
  if (!at) return "downtown";
  const building = manifest.buildings.find((b) => b.building_id === at);
  if (building) return building.zone;
  const npc = manifest.npcs.find((n) => n.instance_id === at);
  if (npc) return npc.zone;
  return "downtown";
}

/** Player-facing clue category label (manifest `type` field). */
export function clueTypeLabel(type: ClueV1["type"]): string {
  switch (type) {
    case "key_evidence":
      return "Key evidence";
    case "red_herring":
      return "Red herring";
    case "background":
      return "Background";
    default:
      return humanizeId(String(type));
  }
}

export function relatedNpcDisplayName(
  manifest: CaseManifestV1,
  clue: ClueLike
): string | null {
  const id = clue.related_npc_instance_id?.trim();
  if (!id) return null;
  return npcDisplayName(manifest, id);
}

export function normalizeManifestClues(manifest: CaseManifestV1): CaseManifestV1 {
  return {
    ...manifest,
    clues: manifest.clues.map((c) => {
      const row = c as ClueLike;
      return { ...c, title: clueFallbackTitle(row) };
    }),
  };
}

export function formatElapsedClock(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDurationAgoFromSeconds(deltaSec: number): string {
  if (deltaSec < 60) return "just now";
  const minutes = Math.floor(deltaSec / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function formatMinutesAgo(createdAtMs: number, nowMs = Date.now()): string {
  const delta = Math.max(0, nowMs - createdAtMs);
  return formatDurationAgoFromSeconds(Math.floor(delta / 1000));
}

export type CaseNoteAgeInput = {
  createdAtMs: number;
  createdAtElapsedSec?: number;
};

export function formatCaseNoteAge(
  note: CaseNoteAgeInput,
  nowElapsedSec: number,
  nowWallMs = Date.now()
): string {
  if (typeof note.createdAtElapsedSec === "number" && Number.isFinite(note.createdAtElapsedSec)) {
    const delta = Math.max(0, nowElapsedSec - note.createdAtElapsedSec);
    return formatDurationAgoFromSeconds(delta);
  }
  return formatMinutesAgo(note.createdAtMs, nowWallMs);
}
